<script setup lang="ts">
const { t } = useI18n();

const rules = [
  { key: 'minNights',  icon: 'lucide:moon' },
  { key: 'prepayment', icon: 'lucide:shield-check' },
  { key: 'checkInOut', icon: 'lucide:clock' },
  { key: 'capacity',   icon: 'lucide:users-round' },
  { key: 'extraBed',   icon: 'lucide:bed' },
  { key: 'longStay',   icon: 'lucide:calendar-range' },
] as const;
</script>

<template>
  <section class="relative overflow-hidden pt-8 md:pt-14 pb-2 md:pb-4 bg-white dark:bg-slate-900">
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -left-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-rose-200/30 blur-3xl dark:bg-rose-500/12"
    />

    <div data-reveal class="relative container">
      <div class="text-center max-w-2xl mx-auto mb-8 md:mb-10">
        <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t('prices.rules.title') }}</span>
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
        </div>
      </div>

      <ul class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <li
          v-for="r in rules"
          :key="r.key"
          class="group relative overflow-hidden rounded-2xl border border-amber-200/50 dark:border-amber-900/40 bg-gradient-to-br from-amber-50/70 via-white to-white dark:from-amber-900/10 dark:via-slate-800/70 dark:to-slate-900/60 p-5 md:p-6 transition duration-300 hover:-translate-y-0.5 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-[0_20px_50px_-24px_rgba(212,175,55,0.35)]"
        >
          <div
            aria-hidden="true"
            class="pointer-events-none absolute -bottom-12 -right-12 size-40 rounded-full bg-amber-200/0 blur-2xl transition-colors duration-500 group-hover:bg-amber-300/30 dark:group-hover:bg-amber-500/15"
          />

          <div class="relative flex items-center gap-3">
            <span
              class="inline-flex size-9 items-center justify-center rounded-full bg-amber-400/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-400/30 transition group-hover:bg-amber-400/30 group-hover:ring-amber-400/60"
            >
              <Icon :name="r.icon" class="size-4" />
            </span>
            <span class="font-display italic text-3xl md:text-4xl leading-none text-amber-700 dark:text-amber-300 tracking-tight">
              {{ t(`prices.rules.stat.${r.key}`) }}
            </span>
            <span class="font-body text-xs md:text-sm text-slate-500 dark:text-slate-400 self-end pb-1">
              {{ t(`prices.rules.unit.${r.key}`) }}
            </span>
          </div>

          <p class="relative mt-3 font-body text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {{ t(`prices.rules.${r.key}`) }}
          </p>
        </li>
      </ul>
    </div>
  </section>
</template>
